import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { login, forgotPassword, resetPassword } from '../services/api';
import toast from 'react-hot-toast';
import { X, ArrowLeft } from 'lucide-react';

// ── Forgot Password Modal (3 steps) ───────────
const ForgotPasswordModal = ({ onClose }) => {
  const [step,        setStep]        = useState(1); // 1=email, 2=otp+new pass, 3=done
  const [email,       setEmail]       = useState('');
  const [otp,         setOtp]         = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm,     setConfirm]     = useState('');
  const [loading,     setLoading]     = useState(false);

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await forgotPassword({ email });
      toast.success('Reset code sent to your email');
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])/.test(newPassword)) {
      toast.error('Password must include uppercase, lowercase, number and special character (@$!%*?&#)');
      return;
    }
    setLoading(true);
    try {
      await resetPassword({ email, otp, newPassword });
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Reset failed. Check your code and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            {step === 2 && (
              <button onClick={() => setStep(1)} className="text-gray-400 hover:text-gray-600 mr-1">
                <ArrowLeft size={18} />
              </button>
            )}
            <h2 className="text-base font-semibold text-gray-900">
              {step === 1 ? 'Reset password' : step === 2 ? 'Enter reset code' : 'Password updated'}
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        {/* Step 1 — Email */}
        {step === 1 && (
          <form onSubmit={handleRequestOtp} className="space-y-4">
            <p className="text-sm text-gray-500">
              Enter your account email. We'll send a 6-digit reset code.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus
                placeholder="you@example.com"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button type="submit" disabled={loading}
              className="w-full bg-blue-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {loading ? 'Sending...' : 'Send reset code'}
            </button>
          </form>
        )}

        {/* Step 2 — OTP + New Password */}
        {step === 2 && (
          <form onSubmit={handleReset} className="space-y-4">
            <p className="text-sm text-gray-500">
              Check your email (<span className="font-medium text-gray-700">{email}</span>) for the 6-digit code.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reset code</label>
              <input
                type="text" value={otp} onChange={e => setOtp(e.target.value)} required autoFocus
                placeholder="123456" maxLength={6}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 tracking-widest text-center text-lg font-mono"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">New password</label>
              <input
                type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required
                placeholder="Min 8 chars, upper, lower, number, symbol"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm password</label>
              <input
                type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required
                placeholder="Repeat password"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button type="submit" disabled={loading}
              className="w-full bg-blue-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {loading ? 'Updating...' : 'Update password'}
            </button>
            <button type="button" onClick={() => handleRequestOtp({ preventDefault: () => {} })}
              className="w-full text-sm text-gray-400 hover:text-blue-600 text-center">
              Didn't receive the code? Resend
            </button>
          </form>
        )}

        {/* Step 3 — Success */}
        {step === 3 && (
          <div className="text-center space-y-4">
            <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center mx-auto">
              <span className="text-green-500 text-2xl">✓</span>
            </div>
            <p className="text-sm text-gray-600">
              Your password has been updated successfully. You can now sign in with your new password.
            </p>
            <button onClick={onClose}
              className="w-full bg-blue-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700">
              Back to sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Login Page ─────────────────────────────────
const Login = () => {
  const [email,        setEmail]        = useState('');
  const [password,     setPassword]     = useState('');
  const [loading,      setLoading]      = useState(false);
  const [showForgot,   setShowForgot]   = useState(false);
  const { loginUser } = useAuth();
  const navigate      = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login({ email, password });
      const { accessToken, refreshToken, user } = res.data.data;
      loginUser({ accessToken, refreshToken }, user);
      toast.success(`Welcome back, ${user.name}!`);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">

      {/* ── Left Panel (dark navy) ── */}
      <div className="hidden md:flex flex-col justify-between w-3/5 bg-gray-900 p-12 text-white">
        <div>
          {/* Logo card */}
          <div className="w-28 h-28 bg-white rounded-2xl flex items-center justify-center mb-10 shadow-lg">
            <img src="/devine-logo.png" alt="Devine Technologies" className="h-20 w-auto object-contain" />
          </div>

          <h1 className="text-3xl font-bold leading-snug mb-4">
            Manpower &amp; Staffing<br />Management System
          </h1>
          <p className="text-gray-400 text-sm leading-relaxed mb-10 max-w-md">
            A complete manpower staffing and payroll management platform — connecting skilled
            professionals with the right opportunities, and bringing your entire workforce
            operation under one roof.
          </p>

          {/* Feature bullets */}
          <div className="space-y-4">
            {[
              'End-to-end candidate lifecycle management',
              'Automated payroll with PF, ESI & TDS compliance',
              'Real-time attendance and leave tracking',
              'Invoice generation and client management',
              'Employee self-service portal',
            ].map(text => (
              <div key={text} className="flex items-start gap-3">
                <span className="text-green-400 font-bold text-base leading-none mt-0.5">✓</span>
                <p className="text-gray-300 text-sm">{text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="text-gray-500 text-xs mt-10 border-t border-gray-700 pt-6">
          <p>Developed by <span className="text-gray-300 font-medium">Devine Technologies</span></p>
          <p className="mt-0.5">Version 1.0.0 · © 2026 Devine Technologies. All rights reserved.</p>
        </div>
      </div>

      {/* ── Right Panel ── */}
      <div className="flex-1 bg-gray-100 flex items-center justify-center p-8">
        {/* Logo (mobile only) */}
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-8 md:hidden">
            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow">
              <img src="/devine-logo.png" alt="Devine Technologies" className="h-7 w-auto object-contain" />
            </div>
            <p className="font-semibold text-gray-900">Manpower Platform</p>
          </div>

          {/* Card */}
          <div className="bg-white rounded-2xl shadow-sm p-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-1">Manpower Staffing Platform</h2>
            <p className="text-gray-500 text-sm mb-7">Sign in to your account</p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-800 bg-white"
                  placeholder="admin@manpower.com"
                  required
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-gray-700">Password</label>
                  <button type="button" onClick={() => setShowForgot(true)}
                    className="text-xs text-gray-500 hover:text-gray-800 font-medium underline underline-offset-2">
                    Forgot password?
                  </button>
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-800 bg-white"
                  placeholder="••••••••"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gray-900 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-gray-800 transition disabled:opacity-50 mt-1"
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </button>
            </form>
          </div>

          <p className="text-center text-xs text-gray-400 mt-5">
            Version 1.0.0
          </p>
        </div>
      </div>

      {showForgot && <ForgotPasswordModal onClose={() => setShowForgot(false)} />}
    </div>
  );
};

export default Login;
